import { useState, type FormEvent } from 'react';
import {
  Button,
  ConfirmDialog,
  Input,
  Modal,
  PageHeader,
  RowMenu,
  StatusBadge,
  Switch,
} from '../../components';
import { ApiError } from '../../lib/api';
import { useCrud } from '../../lib/useCrud';

type ContactCategory = {
  id: string;
  slug: string;
  name: string;
  is_active: boolean;
  sort_order: number;
};

type FormState = { slug: string; name: string; is_active: boolean; sort_order: string };
const toForm = (r?: ContactCategory): FormState => ({
  slug: r?.slug ?? '',
  name: r?.name ?? '',
  is_active: r?.is_active ?? true,
  sort_order: String(r?.sort_order ?? 0),
});
const toBody = (f: FormState) => ({
  slug: f.slug.trim(),
  name: f.name.trim(),
  is_active: f.is_active,
  sort_order: Number(f.sort_order) || 0,
});

export function ContactCategoriesPage() {
  const crud = useCrud<ContactCategory, ReturnType<typeof toBody>, Partial<ReturnType<typeof toBody>>>({
    key: 'contact-categories',
    path: '/settings/contact-categories',
    listQuery: 'include_inactive=true&limit=100',
  });
  const [editing, setEditing] = useState<ContactCategory | 'new' | null>(null);
  const [deleting, setDeleting] = useState<ContactCategory | null>(null);

  return (
    <>
      <PageHeader
        crumb="Settings"
        title="Contact categories"
        actions={
          <Button variant="primary" icon="plus" onClick={() => setEditing('new')}>
            New category
          </Button>
        }
      />
      {crud.list.isLoading && <div className="card card-body muted">Loading…</div>}
      {crud.list.error && <div className="alert">{(crud.list.error as Error).message}</div>}
      {crud.list.data && (
        <div className="card">
          <table className="table">
            <thead>
              <tr>
                <th className="col-num">#</th>
                <th>Name</th>
                <th>Slug</th>
                <th>Sort</th>
                <th>Status</th>
                <th style={{ width: 60 }} />
              </tr>
            </thead>
            <tbody>
              {crud.list.data.data.map((row, i) => (
                <tr key={row.id}>
                  <td className="col-num">{i + 1}</td>
                  <td>{row.name}</td>
                  <td className="cell-mono">{row.slug}</td>
                  <td className="tabular">{row.sort_order}</td>
                  <td>
                    <StatusBadge active={row.is_active} />
                  </td>
                  <td>
                    <RowMenu
                      items={[
                        { icon: 'edit', label: 'Edit', onClick: () => setEditing(row) },
                        'divider',
                        {
                          icon: 'trash',
                          label: 'Delete',
                          onClick: () => setDeleting(row),
                          danger: true,
                        },
                      ]}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <EditModal
          row={editing === 'new' ? undefined : editing}
          onClose={() => setEditing(null)}
          onSubmit={async (body) => {
            if (editing === 'new') await crud.create.mutateAsync(body);
            else await crud.update.mutateAsync({ id: editing.id, body });
            setEditing(null);
          }}
        />
      )}

      <ConfirmDialog
        open={Boolean(deleting)}
        title="Delete contact category"
        message={deleting ? `Delete "${deleting.name}"?` : null}
        destructive
        confirmLabel="Delete"
        loading={crud.remove.isPending}
        onCancel={() => setDeleting(null)}
        onConfirm={async () => {
          if (!deleting) return;
          await crud.remove.mutateAsync(deleting.id);
          setDeleting(null);
        }}
      />
    </>
  );
}

function EditModal({
  row,
  onClose,
  onSubmit,
}: {
  row?: ContactCategory;
  onClose: () => void;
  onSubmit: (b: ReturnType<typeof toBody>) => Promise<void>;
}) {
  const [form, setForm] = useState(() => toForm(row));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handle(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await onSubmit(toBody(form));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Save failed');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal open onClose={onClose} title={row ? 'Edit category' : 'New category'} width={440}>
      <form onSubmit={handle}>
        <div className="card-body" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <Input
            id="cc-name"
            label="Name"
            value={form.name}
            onChange={(v) => setForm({ ...form, name: v })}
            required
            autoFocus
          />
          <Input
            id="cc-slug"
            label="Slug"
            value={form.slug}
            onChange={(v) => setForm({ ...form, slug: v })}
            required
          />
          <div style={{ width: 120 }}>
            <Input
              id="cc-sort"
              label="Sort"
              type="number"
              value={form.sort_order}
              onChange={(v) => setForm({ ...form, sort_order: v })}
            />
          </div>
          <div className="row">
            <Switch
              on={form.is_active}
              onChange={(v) => setForm({ ...form, is_active: v })}
              label="Active"
            />
            <span className="muted">Active</span>
          </div>
          {error && <div className="alert">{error}</div>}
        </div>
        <div
          className="card-header"
          style={{ justifyContent: 'flex-end', borderTop: '1px solid var(--border)', borderBottom: 'none' }}
        >
          <Button onClick={onClose} disabled={submitting} type="button">
            Cancel
          </Button>
          <Button variant="primary" type="submit" disabled={submitting}>
            {submitting ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
